import { DI } from '@intigral/constant/config/DIContainer';
import { IHttpAdapter } from '../../core/entities/iHttpAdapter';
import { IMpxRepository } from '../../core/repositories/interfaces/IMpxRepository';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { API_IDS, MpxAllowedDomains, MpxProgramType } from '@intigral/constant';
import InitiatedError from '../../core/entities/errors/InitiatedError';
import {
    IMpxAllAssetsParams,
    IMpxBiographyResponse,
    IMpxFilmographyResponse,
    IMpxVodResponse,
    IGetCatchUpEpisodeListParams,
    IGetCatchUpEpisodeDetailParams,
    IGetCatchUpEpisodeDetailResponse,
    IGetPPVFeedParams,
    IContentDetailsResponse,
} from '../../entity/interface/iMpxVodResponse';
/* eslint-disable @typescript-eslint/no-inferrable-types */
import { IChannelCategoriesResponse } from '../../entity/interface/iChannelCategoriesResponse';
import { IChannelListByCategoryResponse } from '../../entity/interface/iChannelListByCategoryResponse';
import { APIPath } from '@intigral/constant';

class MpxRepository implements IMpxRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    private get mpxProductFeedBaseURL(): string {
        return process.env['NX_PUBLIC_MPX_PRODUCT_FEED_BASE_URL'] || '';
    }

    private get mpxBaseURL(): string {
        return process.env['NX_PUBLIC_MPX_BASE_URL'] || '';
    }

    private get mpxAccount(): string {
        return process.env['NX_PUBLIC_MPX_ACCOUNT'] || '';
    }

    private get mpxPrefix(): string {
        return process.env['NX_PUBLIC_MPX_PREFIX'] || '';
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    private getFeedUrl(
        feed: string | null = null,
        programType: MpxProgramType = MpxProgramType.Movie
    ): { url: string; params: object } {
        if (feed && feed.indexOf('http') === 0) {
            const url = new URL(feed);
            if (MpxAllowedDomains.includes(url.host) === false) {
                throw new InitiatedError(403, 'Provided an MPX feed URL which is not allowed.');
            }
            const searchParams = new URLSearchParams(url.search);
            const params = Object.fromEntries(searchParams);
            return {
                url: url.href.replace(url.search, ''),
                params: { form: 'cjson', ...params },
            };
        }
        const params: { [key: string]: string | number | boolean } = {
            form: 'cjson',
        };
        if (feed === null) {
            params['byProgramType'] = programType;
        }
        feed = feed ? feed.replace(/^\/+|\/+$/g, '') : APIPath.MPX_ALL_VOD_FEED;
        let GeneratedUrl = feed.includes('all-product') ? this.mpxProductFeedBaseURL : this.mpxBaseURL;
        GeneratedUrl = GeneratedUrl.replace(/\/+$/g, '') + '/';
        GeneratedUrl += this.mpxAccount + '/';
        GeneratedUrl += this.mpxPrefix + feed;
        return { url: GeneratedUrl, params: params };
    }

    async getMovies(
        feed: string | null = null,
        filters: { [key: string]: string | number | boolean }
    ): Promise<IMpxVodResponse> {
        const parsedUrl = this.getFeedUrl(feed, MpxProgramType.Movie);
        const response = await this.http.get<IMpxVodResponse>(parsedUrl.url, {
            params: {
                ...parsedUrl.params,
                ...filters,
            },
        });
        return response.data;
    }

    async getSeries(
        feed: string | null = null,
        filters: { [key: string]: string | number | boolean }
    ): Promise<IMpxVodResponse> {
        const parsedUrl = this.getFeedUrl(feed, MpxProgramType.Series);
        const response = await this.http.get<IMpxVodResponse>(parsedUrl.url, {
            params: {
                ...parsedUrl.params,
                ...filters,
            },
        });
        return response.data;
    }

    async getEpisodes(byTvSeasonId: string, byCustomValue: string): Promise<IMpxVodResponse> {
        const path = this.apiPathManager.getPath(API_IDS.GET_ALL_EPISODES);
        const response = await this.http.get<any>(path, {
            params: {
                byTvSeasonId,
                byCustomValueCountry: byCustomValue,
            },
        });
        return response.data;
    }

    async getFeed(feed: string, filters: { [key: string]: string | number | boolean }): Promise<unknown> {
        const parsedUrl = this.getFeedUrl(feed);
        const response = await this.http.get<unknown>(parsedUrl.url, {
            params: {
                ...parsedUrl.params,
                ...filters,
            },
        });
        return response.data;
    }

    async getBiography(feed: string): Promise<IMpxBiographyResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MPX_BIOGRAPHY);
        const response = await this.http.get<IMpxBiographyResponse>(path);
        return response.data;
    }

    async getFilmography(byCustomValue: string, personId: string): Promise<IMpxFilmographyResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MPX_FILMOGRAPHY);
        const response = await this.http.get<IMpxFilmographyResponse>(path, {
            params: {
                byCustomValueCountry: byCustomValue,
                byPersonId: personId,
            },
        });
        return response.data;
    }

    async getProducts(feed: string, filters: { [key: string]: string | number | boolean }): Promise<unknown> {
        const parsedUrl = this.getFeedUrl(feed);
        const response = await this.http.get<unknown>(parsedUrl.url, {
            params: {
                ...parsedUrl.params,
                ...filters,
            },
        });
        return response.data;
    }
    async getMpxAllAssets(params: IMpxAllAssetsParams): Promise<any> {
        const response = await this.http.get<any>(this.apiPathManager.getPath(API_IDS.GET_ITEM_BY_ID), {
            params: {
                ...params,
            },
        });
        return response.data;
    }

    async getPPVFeed(params: IGetPPVFeedParams): Promise<any> {
        const response = await this.http.get<any>(this.apiPathManager.getPath(API_IDS.GET_PPV_FEED), {
            params: {
                ...params,
            },
        });
        return response.data;
    }

    async getMpxCastCrew(byCredits: string): Promise<any> {
        const path = this.apiPathManager.getPath(API_IDS.GET_CAST_CREW);
        const replacedParams = path.replace('&byTitlePrefix={title}', '');
        const response = await this.http.get<any>(replacedParams, {
            params: {
                byCredits,
            },
        });
        return response.data;
    }

    async getSeasons(bySeriesId: string, byCustomValue: string): Promise<any> {
        const path = this.apiPathManager.getPath(API_IDS.GET_ALL_SEASONS);
        const response = await this.http.get<any>(path, {
            params: {
                bySeriesId,
                byCustomValue,
            },
        });
        return response.data;
    }

    async getChannelCategories(
        guids: string,
        country: string,
        isKidsMode: boolean
    ): Promise<IChannelCategoriesResponse> {
        let path = this.apiPathManager.getPath(API_IDS.MPX_GET_CHANNEL_CATEGORIES_API);

        if (isKidsMode) {
            path = `${path},{categoryType}{kids}`;
        }

        const response = await this.http.get<IChannelCategoriesResponse>(path, {
            params: {
                bySubscriptionGuidsPipe: guids,
                countryCode: country,
            },
        });
        return response.data;
    }

    async getChannelListByCategory(category: string): Promise<IChannelListByCategoryResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MPX_GET_CHANNEL_LIST_BY_CATEGORY_API);
        const response = await this.http.get<IChannelListByCategoryResponse>(path, {
            params: {
                category,
            },
        });
        return response.data;
    }

    async getCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse> {
        const path = this.apiPathManager.getPath(API_IDS.CATCH_UP_EPISODE_LIST);
        const response = await this.http.get<IMpxVodResponse>(path, {
            params: {
                ...params,
            },
        });
        return response.data;
    }
    async getMixedCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MIXED_CATCH_UP_EPISODE_LIST);
        const response = await this.http.get<IMpxVodResponse>(path, {
            params: {
                ...params,
            },
        });
        return response.data;
    }

    async getCatchUpEpisodeDetail(params: IGetCatchUpEpisodeDetailParams): Promise<IGetCatchUpEpisodeDetailResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MPX_GET_ALL_LIST);
        const response = await this.http.get<IGetCatchUpEpisodeDetailResponse>(path, {
            params: {
                ...params,
            },
        });
        return response.data;
    }

    async getContentDetailByGuid(guid: string): Promise<IContentDetailsResponse> {
        const path = this.apiPathManager.getPath(API_IDS.MPX_CONTENT_DETAILS);
        const response = await this.http.get<IContentDetailsResponse>(path, {
            params: {
                movieId: guid,
            },
        });
        return response.data;
    }
}

export default MpxRepository;
